import React, { useEffect } from 'react';
import { Button, Container, Grid, Link, Typography, Theme, Box, Tooltip } from '@mui/material';
import { Rating, ProfileAvatar, Badge, useActiveTheme } from '@care/react-component-lib';
import { Icon24InfoBgcFilled } from '@care/react-icons';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Icon24ShieldFilled } from '@care/react-embrace-icons';
import { SxClassProps } from '@/types/common';
import { getUserInitials } from '@/utils/getUserInitials';
import payRangeHelper from '@/utils/payRangeHelper';

import { AmplitudeEvent, trackAmplitudeEvent } from '@/lib/analyticsHelper';
import {
  getProviderDetails_getSEOSegmentationCaregiverDetails_caregiver_member_address as Address,
  getProviderDetails_getSEOSegmentationCaregiverDetails_caregiver_profiles_commonCaregiverProfile_payRange as PayRange,
} from '@/__generated__/getProviderDetails';
import { LaunchDarklyEvaluationReasons, UI_FEATURE_FLAGS } from '@/constants';
import useFeatureFlag from './hooks/useFeatureFlag';
import { usePageAttributes } from './contexts/PageAttributesContext';
import useBackgroundCheckModal from './hooks/useBackgroundCheckModal';
import { SEOSegmentationPageType } from '../__generated__/globalTypes';

const getClasses = (yearsOfExperience: number): SxClassProps => {
  return {
    profileContainer: (theme) => ({
      [theme.breakpoints.up('md')]: {
        maxWidth: '780px',
        padding: theme.spacing(0, 3),
      },
    }),
    profileImgSection: (theme) => ({
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      marginTop: theme.spacing(4),
      [theme.breakpoints.up('md')]: {
        marginTop: theme.spacing(0),
        maxWidth: '315px',
        justifyContent: 'flex-start',
      },
    }),
    profileImgCustomBig: (theme) => ({
      '& .MuiAvatar-root': {
        width: '120px',
        height: '120px',
        fontSize: '48px',

        [theme.breakpoints.up('md')]: {
          width: '288px',
          height: '288px',
          fontSize: '72px',
        },
      },
    }),
    profileAvailability: (theme) => ({
      color: theme.palette.care?.grey[600],
    }),
    profileInfoSection: (theme) => ({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      [theme.breakpoints.up('md')]: {
        justifyContent: 'flex-start',
        maxWidth: '300px',
      },
    }),
    ageAndExpContainer: {
      maxWidth: '200px',
    },
    ageAndExp: (theme) => ({
      borderRight: yearsOfExperience > 0 ? `2px solid ${theme.palette.care?.grey[300]}` : 'none',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      marginTop: theme.spacing(2),
      [theme.breakpoints.up('md')]: {
        alignItems: 'flex-start',
      },
      '&:last-of-type': {
        borderRight: '0',
        [theme.breakpoints.up('md')]: {
          alignItems: 'center',
        },
      },
    }),
    bgcButton: {
      padding: 0,
      height: 'inherit',
      width: 'inherit',
    },
    careBadge: (theme) => ({
      justifyContent: 'center',
      display: 'flex',
      cursor: 'pointer',
      marginTop: theme.spacing(1.8),
      '& a:focus': {
        outline: 'none',
      },
      [theme.breakpoints.up('md')]: {
        justifyContent: 'left',
      },
    }),
    profileInfo: (theme) => ({
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      margin: theme.spacing(1),

      [theme.breakpoints.up('md')]: {
        margin: theme.spacing(5, 'auto'),
        width: '290px',
        textAlign: 'left',
        alignItems: 'flex-start',
      },
    }),
    profileTitle: (theme) => ({
      marginTop: theme.spacing(1),
      [theme.breakpoints.up('md')]: {
        marginBottom: theme.spacing(0),
      },
    }),
    profileRating: (theme) => ({
      margin: theme.spacing(1.25),
      display: 'flex',
      marginLeft: '0',
    }),
    ratingStars: {
      fontSize: '16px',
    },
    joinBtn: (theme) => ({
      minWidth: '215px',
      width: 'auto',
      marginTop: theme.spacing(3),
      [theme.breakpoints.up('md')]: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(1),
      },
    }),
  };
};

interface ProfileProps {
  displayName: string;
  firstName: string;
  hiResImageURL: string;
  address: Address;
  joinNowURL: string;
  hasCareCheck: boolean;
  yearsOfExperience: number;
  numberOfReviews: number;
  avgReviewRating: number;
  payRange: PayRange;
  availability: string;
}

const Profile = ({
  displayName,
  firstName,
  hiResImageURL,
  address,
  joinNowURL,
  hasCareCheck,
  yearsOfExperience,
  numberOfReviews,
  avgReviewRating,
  payRange,
  availability,
}: ProfileProps) => {
  const classes = getClasses(yearsOfExperience);
  const { serviceIdForMember, pageType } = usePageAttributes();
  const [BadgeModal, setOpen] = useBackgroundCheckModal(false);
  const isMobileOrSmaller = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const payRangeString = payRangeHelper(
    payRange.hourlyRateTo.amount,
    payRange.hourlyRateFrom.amount
  );
  const { isEmbraceTheme } = useActiveTheme();

  const personalJoinButtonFlag = useFeatureFlag(
    UI_FEATURE_FLAGS.SEO_1265_PROVIDER_PROFILE_PERSONALIZED_CTA.id
  );

  const isPersonalJoinButtonTest = personalJoinButtonFlag?.variationIndex === 1;
  const joinToContactText = isPersonalJoinButtonTest ? `Contact ${firstName}` : 'Join to contact';
  const joinNowText = isPersonalJoinButtonTest ? `Contact ${firstName}` : 'Join now';

  useEffect(() => {
    if (
      personalJoinButtonFlag &&
      personalJoinButtonFlag.reason.kind === LaunchDarklyEvaluationReasons.RULE_MATCH
    ) {
      trackAmplitudeEvent(AmplitudeEvent.testExposure, {
        test_name: UI_FEATURE_FLAGS.SEO_1265_PROVIDER_PROFILE_PERSONALIZED_CTA.id,
        test_variant: personalJoinButtonFlag.variationIndex,
        service_id_for_member: serviceIdForMember,
        page_template: pageType === SEOSegmentationPageType.NATIONAL ? 'National' : 'Local',
      });
    }
  });

  const handleJoinToContact = (e: React.MouseEvent<HTMLButtonElement>) => {
    const amplitudeData = {
      cta_text: (e.target as HTMLElement).textContent as string,
      cta_type: 'button',
    };

    trackAmplitudeEvent(AmplitudeEvent.ctaInteracted, amplitudeData);
  };

  const handleBackgroundCheck = () => {
    setOpen(true);

    const amplitudeData = {
      cta_intent: 'trust badge',
      cta_text: 'background check',
    };

    trackAmplitudeEvent(AmplitudeEvent.ctaInteracted, amplitudeData);
  };

  return (
    <Container maxWidth="md" disableGutters>
      <Grid container sx={classes.profileContainer}>
        <Grid item xs={12} lg={6} sx={classes.profileImgSection}>
          <ProfileAvatar
            alt={displayName}
            sx={classes.profileImgCustomBig}
            size="squareMedium"
            src={hiResImageURL}>
            {getUserInitials(displayName)}
          </ProfileAvatar>
        </Grid>
        <Grid item xs={12} lg={5} sx={classes.profileInfoSection}>
          <Box sx={classes.profileInfo}>
            {availability && (
              <Typography variant="tag" sx={classes.profileAvailability}>
                {availability?.replace('_', '-')}
              </Typography>
            )}
            <Typography variant={isMobileOrSmaller ? 'h1' : 'display3'} sx={classes.profileTitle}>
              {displayName}
            </Typography>
            <Box sx={classes.profileRating}>
              <Rating name="size-large" defaultValue={avgReviewRating} readOnly />
              <Typography variant="info1" color="secondary">
                ({numberOfReviews})
              </Typography>
            </Box>
            <Typography variant={isMobileOrSmaller ? 'body3' : undefined}>
              {`${address?.city}, ${address?.state}`}
            </Typography>
            <Grid container justify-content="center" sx={classes.ageAndExpContainer}>
              {payRangeString && (
                <Grid item xs={yearsOfExperience > 0 ? 6 : 12} sx={classes.ageAndExp}>
                  <Typography variant="h2">{payRangeString}</Typography>
                  <Typography variant="body3" color="secondary">
                    Hourly rate
                  </Typography>
                </Grid>
              )}
              <Grid item xs={6} sx={classes.ageAndExp}>
                {yearsOfExperience !== 0 && (
                  <>
                    <Typography variant="h2">{yearsOfExperience}</Typography>
                    <Typography variant="body3" color="secondary">
                      Years exp.
                    </Typography>
                  </>
                )}
              </Grid>
            </Grid>
            {hasCareCheck ? (
              <>
                <Grid item xs={12} sx={classes.careBadge}>
                  {isEmbraceTheme ? (
                    <Tooltip
                      PopperProps={{
                        disablePortal: true,
                      }}
                      title="Completed a Care.com Background Check"
                      placement="bottom-start"
                      arrow>
                      <Badge
                        icon={<Icon24ShieldFilled />}
                        text="Background checked"
                        sx={{
                          ml: 0.5,
                          mr: 0,
                          cursor: 'pointer',
                          height: '100%',
                          position: 'relative',
                          top: '3px',
                        }}
                      />
                    </Tooltip>
                  ) : (
                    <Badge
                      aria-label="Background Check"
                      onClick={handleBackgroundCheck}
                      icon={<Icon24InfoBgcFilled size="18px" />}
                      text="Background Check"
                    />
                  )}
                </Grid>
                <Link href={joinNowURL} rel="nofollow">
                  <Button
                    sx={classes.joinBtn}
                    color="primary"
                    onClick={handleJoinToContact}
                    variant="contained">
                    {joinToContactText}
                  </Button>
                </Link>
              </>
            ) : (
              <Link href={joinNowURL} rel="nofollow">
                <Button
                  sx={classes.joinBtn}
                  color="primary"
                  onClick={handleJoinToContact}
                  variant="contained">
                  {joinNowText}
                </Button>
              </Link>
            )}
          </Box>
        </Grid>
      </Grid>
      <BadgeModal />
    </Container>
  );
};

export default Profile;
